import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, X } from 'lucide-react';
import { cn } from '@/infrastructure/lib/utils';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { useIsSuperAdminRole } from '@/shared/hooks/use-system-role';

/** `transition-transform duration-300` da `<aside>` em Sidebar.tsx. */
const SIDEBAR_TRANSITION_MS = 320;
const VISIBLE_MS = 6000;

/**
 * Aviso apontando para o seletor de organizações, logo após aceitar um convite
 * (AB#84509).
 *
 * Quem ganha a segunda organização não tem como adivinhar que a troca mora no card do
 * rodapé — é um controle que, até aquele instante, não existia para essa pessoa.
 *
 * Aparece só no momento do aceite, e não por ter várias organizações: superadmin
 * sempre teve muitas e sempre soube trocar; mostrar para ele seria ruído. Some sozinho
 * em alguns segundos, como o lembrete do tour, do qual este reusa a forma.
 *
 * Renderiza DENTRO do container do card, ancorado por `bottom-full`, e não em posição
 * calculada: card e barra têm transições próprias, então qualquer medida por
 * `getBoundingClientRect` é tirada de um alvo em movimento e congela numa posição que
 * já não é a dele. Ancorado no DOM, ele acompanha o card sem cálculo nenhum.
 *
 * Se a barra lateral estiver fechada no momento do aceite, o aviso fica guardado e
 * espera ela abrir — apontar para um card fora da tela não ensina nada.
 */
export const OrgSwitcherHint = ({ sidebarOpen }: { sidebarOpen: boolean }) => {
  const { t } = useTranslation();
  const { justJoinedOrganization, clearJustJoinedOrganization } = useSuperAdminOrg();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const [show, setShow] = useState(false);

  const isOpen = !!justJoinedOrganization && !isSuperAdmin && sidebarOpen;

  // O timer que dispensa o aviso nasce DENTRO de outro timer e sobrevive ao
  // cleanup do efeito se não for guardado — bastava fechar e reabrir a barra para
  // um timer antigo limpar o aviso recém-aberto.
  const dismissTimer = useRef<ReturnType<typeof setTimeout>>();
  const scheduleDismiss = useCallback(() => {
    clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(clearJustJoinedOrganization, 300);
  }, [clearJustJoinedOrganization]);

  useEffect(() => () => clearTimeout(dismissTimer.current), []);

  useEffect(() => {
    // Um dispensar pendente da sessão anterior mataria este aviso 300ms depois de
    // ele abrir — janela curta, mas alcançável ao fechar e reabrir a barra.
    clearTimeout(dismissTimer.current);

    if (!isOpen) {
      setShow(false);
      return;
    }

    // Entra depois que a barra termina de deslizar, para o aviso não viajar junto.
    const enter = setTimeout(() => setShow(true), SIDEBAR_TRANSITION_MS);
    const leave = setTimeout(() => {
      setShow(false);
      scheduleDismiss();
    }, SIDEBAR_TRANSITION_MS + VISIBLE_MS);

    return () => {
      clearTimeout(enter);
      clearTimeout(leave);
    };
  }, [isOpen, scheduleDismiss]);

  if (!isOpen) return null;

  const close = () => {
    setShow(false);
    scheduleDismiss();
  };

  return (
    <div
      className={cn(
        'absolute bottom-full left-0 right-0 z-50 mb-3 transition-all duration-300',
        show ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'
      )}
    >
      <div className="relative rounded-lg bg-primary px-3 py-2.5 text-primary-foreground shadow-lg">
        <button
          type="button"
          onClick={close}
          className="absolute right-1.5 top-1.5 rounded-md p-1 text-primary-foreground/70 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground focus:outline-none"
          aria-label={t('common.close', 'Fechar')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="flex items-center gap-2.5 pr-5">
          <Building2 className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm leading-snug">
            <span className="font-semibold">
              {t('navigation.orgHint.title', { organization: justJoinedOrganization })}
            </span>{' '}
            <span className="opacity-90">{t('navigation.orgHint.message')}</span>
          </p>
        </div>

        {/* Seta apontando para baixo, na direção do card */}
        <div className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-primary" />
      </div>
    </div>
  );
};
